/**
 * CheckpointSystem.ts
 * Checkpoint activation and death-restore logic for Starfall Courier.
 *
 * A checkpoint is a named world-space rectangle. When Nova overlaps it and it
 * has not yet been activated this session, the system:
 *   1. Sets the active checkpoint ID on the session state.
 *   2. Updates the spawn position so the next respawn starts here.
 *   3. Calls SaveManager.save() asynchronously (never blocks the game loop).
 *
 * On death (handled by physicsStep → respawn.ts/respawnNova), the game reads
 * `gameState.checkpointSpawnX/Y` to decide where to respawn. PlayScene.update()
 * keeps these in sync with the active checkpoint via getRespawnPosition().
 *
 * Architecture: NO React. NO async in update(). Save is fire-and-forget.
 *
 * Phase 2 — Combat, Enemies, Checkpoints, HUD.
 * See ROADMAP.md Phase 2.
 *
 * @module CheckpointSystem
 */

import { aabbOverlaps } from '../engine/collision';
import type { AABB } from '../types/entity';
import type { NovaRuntime } from '../engine/gameState';
import { NOVA_WIDTH, NOVA_HEIGHT } from '../../constants/gameConstants';
import { save, load, createDefaultSave } from '../services/saveManager';
import { dispatch } from '../engine/gameEventDispatch';
import Logger from '../utils/logger';

/**
 * Module-level set of activated checkpoint IDs.
 * CheckpointSystem._activate() populates this; the renderer reads it
 * to draw the visual feedback (glow/scale) on activated checkpoints.
 * Cleared when a new CheckpointSystem is constructed.
 */
export const activatedCheckpointIds: Set<string> = new Set();

/** A named checkpoint rectangle in world space. */
export interface Checkpoint {
  /** Unique identifier for this checkpoint within the level. */
  id: string;
  /** World-space AABB — the trigger zone Nova must overlap to activate. */
  bounds: AABB;
  /** Nova's respawn X when this checkpoint is active (usually bounds.x). */
  spawnX: number;
  /** Nova's respawn Y when this checkpoint is active. */
  spawnY: number;
}

/**
 * CheckpointSystem — stateful checkpoint tracker for one level session.
 *
 * Instantiate once per level load (in PlayScene.init). Call `update()` each
 * physics tick with Nova's current position.
 *
 * @example
 * ```ts
 * const checkpoints = new CheckpointSystem('level-1-1', cpList);
 * // each tick:
 * checkpoints.update(nova);
 * // on respawn:
 * const { x, y } = checkpoints.getRespawnPosition(defaultX, defaultY);
 * ```
 */
export class CheckpointSystem {
  /**
   * The level ID used as the key in SaveData.levels.
   * Should follow the format "${worldId}-${levelId}" as documented in save.ts,
   * e.g. "1-1" for World 1, Level 1.
   */
  private readonly _levelId: string;
  /** All checkpoints in this level, ordered left-to-right. */
  private readonly _checkpoints: Checkpoint[];
  /** Cached reference to the active checkpoint, or null. */
  private _activeCheckpoint: Checkpoint | null = null;
  /** Set of IDs activated this session — prevents re-saves on re-overlap. */
  private readonly _activated = new Set<string>();
  // DECISION: Serialise checkpoint saves via promise chain to prevent race
  // conditions where a later checkpoint's save completes before an earlier one.
  private _saveChain: Promise<void> = Promise.resolve();

  constructor(levelId: string, checkpoints: Checkpoint[]) {
    this._levelId = levelId;
    // DECISION: Sort by x-position so array index equals progression order.
    // TileMapLoader builds the list row-major, which can mis-order checkpoints
    // on levels with multi-row / elevated checkpoint placements.
    this._checkpoints = [...checkpoints].sort((a, b) => a.spawnX - b.spawnX);
    // Clear module-level activated set for fresh session.
    activatedCheckpointIds.clear();
  }

  /**
   * Check whether Nova overlaps any checkpoint and activate newly entered ones.
   * Safe to call every physics tick — skips already-activated checkpoints cheaply.
   *
   * @param nova - Current Nova runtime state.
   */
  public update(nova: NovaRuntime): void {
    const novaBox: AABB = { x: nova.x, y: nova.y, width: NOVA_WIDTH, height: NOVA_HEIGHT };
    for (const cp of this._checkpoints) {
      if (this._activated.has(cp.id)) continue;
      if (aabbOverlaps(novaBox, cp.bounds)) {
        this._activate(cp);
        // DECISION: Break after first activation — prevents multiple checkpoints
        // activating in a single tick if their bounds overlap.
        break;
      }
    }
  }

  /**
   * Return the respawn position: the active checkpoint's spawn, or the level
   * default if no checkpoint has been activated yet.
   * DECISION: Uses cached _activeCheckpoint reference — O(1) instead of O(n) find.
   */
  public getRespawnPosition(defaultX: number, defaultY: number): { x: number; y: number } {
    const cp = this._activeCheckpoint;
    return cp ? { x: cp.spawnX, y: cp.spawnY } : { x: defaultX, y: defaultY };
  }

  /** The ID of the currently active checkpoint, or null. */
  public get activeCheckpointId(): string | null {
    return this._activeCheckpoint?.id ?? null;
  }

  /**
   * Restore the active checkpoint from a saved game.
   * Call during PlayScene.init if a save exists for this level.
   *
   * @param savedId - The `lastCheckpointId` from the saved LevelProgress.
   */
  public restoreFromSave(savedId: string | null): void {
    if (!savedId) return;
    const cpIndex = this._checkpoints.findIndex(c => c.id === savedId);
    if (cpIndex < 0) return;
    this._activeCheckpoint = this._checkpoints[cpIndex];
    // DECISION: Mark the restored checkpoint AND all earlier ones as activated.
    // Without this, walking back over a prior checkpoint would regress the save.
    for (let i = 0; i <= cpIndex; i++) {
      this._activated.add(this._checkpoints[i].id);
      activatedCheckpointIds.add(this._checkpoints[i].id);
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private _activate(cp: Checkpoint): void {
    this._activeCheckpoint = cp;
    this._activated.add(cp.id);
    activatedCheckpointIds.add(cp.id);
    dispatch.checkpointActivated();
    Logger.info('CheckpointSystem', `Checkpoint activated: ${cp.id}`);
    // DECISION: Fire-and-forget save — chained via _saveChain to serialise
    // concurrent checkpoint activations and prevent write-order races.
    // All I/O (localStorage read/write) happens on later event-loop ticks.
    // This satisfies AGENTS.md's "never block the game loop" requirement while
    // still guaranteeing eventual persistence.
    // DECISION: .catch() before .then() ensures a failed earlier save does not
    // prevent subsequent checkpoint activations from persisting.
    // DECISION: Inner .catch() on _persistAsync ensures the last activation in
    // the chain also has error handling (avoids unhandled rejection).
    this._saveChain = this._saveChain
      .catch(err => Logger.error('CheckpointSystem', 'Save failed', err))
      .then(() => this._persistAsync(cp.id))
      .catch(err => Logger.error('CheckpointSystem', 'Save failed', err));
  }

  private async _persistAsync(checkpointId: string): Promise<void> {
    const existing = (await load()) ?? createDefaultSave();
    // DECISION: _levelId is passed in full format (e.g. "1-1") by the caller.
    // See save.ts documentation for the expected key format.
    existing.levels[this._levelId] = {
      ...(existing.levels[this._levelId] ?? {
        completed: false,
        collectedConstellationShardIds: [],
        collectedStarSeedIds: [],
        collectedHeartFragmentIds: [],
      }),
      lastCheckpointId: checkpointId,
    };
    await save(existing);
  }
}
